import { isSensitiveLogKey } from './redaction'
import type { ObservabilityContext } from './types'

const MAX_CONTEXT_VALUES = 32
const MAX_CONTEXT_KEYS = 128

const CONTEXT_KEYS = new Set<keyof ObservabilityContext>([
  'requestId',
  'route',
  'method',
  'statusCode',
  'operationId',
  'orgId',
  'brandId',
  'userId',
  'chatId',
  'emailId',
  'automationId',
  'executionId',
  'sendId',
  'workflowName',
  'workflowRunId',
  'workflowStepId',
  'workflowAttempt',
])

const CONTEXT_KEY_ALIASES = new Map<string, keyof ObservabilityContext>([
  ['actingUserId', 'userId'],
  ['adminUserId', 'userId'],
  ['auditId', 'operationId'],
  ['importId', 'operationId'],
  ['jobId', 'operationId'],
  ['resultId', 'operationId'],
])

/** Fixed-key extraction for hot paths; never enumerates or traverses a payload. */
export function observabilityContextFromRecord(
  value: unknown
): ObservabilityContext {
  const context: ObservabilityContext = {}
  if (!value || typeof value !== 'object') {
    return context
  }
  const record = value as Record<string, unknown>
  const copy = (source: string, target: keyof ObservabilityContext) => {
    try {
      const candidate = record[source]
      if (target === 'workflowAttempt' || target === 'statusCode') {
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
          Object.assign(context, { [target]: candidate })
        }
      } else if (typeof candidate === 'string') {
        Object.assign(context, { [target]: candidate })
      }
    } catch {
      // Optional diagnostic getters must not interrupt the owning operation.
    }
  }
  for (const key of CONTEXT_KEYS) {
    copy(key, key)
  }
  for (const [source, target] of CONTEXT_KEY_ALIASES) {
    if (context[target] === undefined) {
      copy(source, target)
    }
  }
  return context
}

/**
 * Infer legacy context with a shared key budget, skipping private payloads.
 * Explicit context remains authoritative over identifiers found in details.
 */
export function observabilityContextFromValues(
  values: Array<unknown>,
  base: ObservabilityContext | undefined
): ObservabilityContext {
  const context: ObservabilityContext = {}
  const explicitContext = observabilityContextFromRecord(base)
  const visited = new WeakSet<object>()
  let remainingKeys = MAX_CONTEXT_KEYS
  const visit = (value: unknown, depth: number): void => {
    if (
      remainingKeys === 0 ||
      depth > 2 ||
      !value ||
      typeof value !== 'object' ||
      visited.has(value)
    ) {
      return
    }
    visited.add(value)
    try {
      if (value instanceof Error || Array.isArray(value)) {
        return
      }
      for (const key in value) {
        if (!Object.hasOwn(value, key)) {
          continue
        }
        if (remainingKeys === 0) {
          break
        }
        remainingKeys -= 1
        if (isSensitiveLogKey(key)) {
          continue
        }
        try {
          const isCanonicalKey = CONTEXT_KEYS.has(
            key as keyof ObservabilityContext
          )
          const contextKey = isCanonicalKey
            ? (key as keyof ObservabilityContext)
            : CONTEXT_KEY_ALIASES.get(key)
          if (
            contextKey &&
            (explicitContext[contextKey] !== undefined ||
              (!isCanonicalKey && context[contextKey] !== undefined))
          ) {
            continue
          }
          const entry = (value as Record<string, unknown>)[key]
          if (!contextKey) {
            visit(entry, depth + 1)
          } else if (
            contextKey === 'workflowAttempt' ||
            contextKey === 'statusCode'
          ) {
            if (typeof entry === 'number' && Number.isFinite(entry)) {
              Object.assign(context, { [contextKey]: entry })
            }
          } else if (typeof entry === 'string') {
            Object.assign(context, { [contextKey]: entry })
          }
        } catch {
          // Skip unavailable metadata while retaining other useful context.
        }
      }
    } catch {
      // Proxies may reject enumeration; context inference is best effort.
    }
  }
  for (
    let index = 0;
    index < Math.min(values.length, MAX_CONTEXT_VALUES);
    index += 1
  ) {
    visit(values[index], 0)
  }
  return { ...context, ...explicitContext }
}
